import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { 
  Mic2, Play, Pause, Loader2, 
  Headphones, Search, ChevronRight, X,
  Radio, Calendar, ChevronLeft, Layout,
  Eye, ShieldCheck, Globe, Code, Clock,
  Podcast as PodcastIcon, Activity, Target,
  Sparkles, LayoutGrid, List, Shield, ShieldAlert,
  BookOpen, Layers, Volume2, GraduationCap
} from 'lucide-react';
import { StorageService } from '../services/storageService';
import { Podcast } from '../types';
import { AdUnit } from '../components/AdUnit';
import { useAudio } from '../components/AudioContext';
import { ModuleHeader } from '../components/ModuleHeader';
import { SponsoredService } from '../services/sponsoredService';
import { useThemeTokens } from '../hooks/useActiveTheme';
import { PageLoader } from '../components/PageLoader';

interface RSSEpisode {
    title: string;
    description: string;
    imageUrl: string;
    sourceUrl: string;
    duration: string;
    pubDate: string;
}

// Dynamic category resolver according to podcasts definition
const getPodcastCategory = (podcast: Podcast) => {
  const title = podcast.title.toLowerCase();
  const desc = podcast.description.toLowerCase();
  
  if (
    title.includes('infection') || title.includes('precautions') || title.includes('safety') || title.includes('isolation') || title.includes('glove') || title.includes('ppe') || title.includes('wash') ||
    desc.includes('infection') || desc.includes('safety') || desc.includes('hygiene')
  ) {
    return {
      name: 'Safety & Infection',
      icon: ShieldAlert,
      color: 'rose',
      badgeColor: 'bg-rose-50 border-rose-100 text-rose-700 dark:bg-rose-950/25 dark:border-rose-900/50 dark:text-rose-400',
      hoverColor: 'hover:border-rose-300 dark:hover:border-rose-800',
      textColor: 'text-rose-600 dark:text-rose-450'
    };
  }
  
  if (
    title.includes('adl') || title.includes('bath') || title.includes('elimination') || title.includes('urinary') || title.includes('bowel') || title.includes('groom') || title.includes('dress') || title.includes('hygiene') || title.includes('personal') ||
    desc.includes('adl') || desc.includes('groom') || desc.includes('care') || desc.includes('personal')
  ) {
    return {
      name: 'Personal ADL Care',
      icon: BookOpen,
      color: 'amber',
      badgeColor: 'bg-amber-50 border-amber-100 text-amber-700 dark:bg-amber-950/25 dark:border-amber-900/50 dark:text-amber-450',
      hoverColor: 'hover:border-amber-300 dark:hover:border-amber-800',
      textColor: 'text-amber-605 dark:text-amber-450'
    };
  }
  
  if (
    title.includes('vital') || title.includes('weight') || title.includes('pulse') || title.includes('blood pressure') || title.includes('temperature') || title.includes('conversion') || title.includes('nutrition') || title.includes('hydration') ||
    desc.includes('vital') || desc.includes('nutrition') || desc.includes('hydration') || desc.includes('pressure')
  ) {
    return {
      name: 'Clinical & Vitals',
      icon: Activity,
      color: 'emerald',
      badgeColor: 'bg-emerald-50 border-emerald-100 text-emerald-700 dark:bg-emerald-950/25 dark:border-emerald-900/50 dark:text-emerald-400',
      hoverColor: 'hover:border-emerald-300 dark:hover:border-emerald-800',
      textColor: 'text-emerald-600 dark:text-emerald-400'
    };
  }

  return {
    name: 'Board Prep & Basics',
    icon: GraduationCap,
    color: 'indigo',
    badgeColor: 'bg-indigo-50 border-indigo-100 text-indigo-700 dark:bg-indigo-950/25 dark:border-indigo-900/50 dark:text-indigo-400',
    hoverColor: 'hover:border-indigo-300 dark:hover:border-indigo-800',
    textColor: 'text-indigo-600 dark:text-indigo-405'
  };
};

// Format RSS duration safely, translating numeric raw seconds to HH:MM:SS
const formatRssDuration = (dur: string) => {
  if (!dur) return "--:--";
  if (/^\d+$/.test(dur)) {
    const secs = parseInt(dur, 10);
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3605) / 60);
    const s = secs % 60;
    const parts = [];
    if (h > 0) parts.push(h);
    parts.push(m.toString().padStart(2, '0'));
    parts.push(s.toString().padStart(2, '0'));
    return parts.join(':');
  }
  return dur;
};

export const Podcasts: React.FC = () => {
  const { certId } = useParams<{ certId: string }>();
  const { playTrack, currentTrack, isPlaying, togglePlay } = useAudio();
  const [config, setConfig] = useState(SponsoredService.getConfig());
  const tokens = useThemeTokens();

  useEffect(() => {
    const handleUpdate = () => {
      setConfig(SponsoredService.getConfig());
    };
    window.addEventListener('sponsored-config-updated', handleUpdate);
    return () => window.removeEventListener('sponsored-config-updated', handleUpdate);
  }, []);

  const getPodcastCardBgClass = (colorName: string) => {
    const isVibrant = config?.enableVibrantCards !== false;
    if (!isVibrant) {
      return 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-850';
    }
    switch (colorName) {
      case 'rose':
        return 'bg-gradient-to-br from-rose-50/70 via-rose-50/30 to-rose-100/10 border-rose-200/80 dark:from-slate-900/90 dark:via-rose-950/35 dark:to-rose-950/55 dark:border-rose-800/60 shadow-[0_4px_20px_-4px_rgba(244,63,94,0.08)]';
      case 'amber':
        return 'bg-gradient-to-br from-amber-50/70 via-amber-50/30 to-amber-100/10 border-amber-200/80 dark:from-slate-900/90 dark:via-amber-950/35 dark:to-amber-950/55 dark:border-amber-800/60 shadow-[0_4px_20px_-4px_rgba(245,158,11,0.08)]';
      case 'emerald':
        return 'bg-gradient-to-br from-emerald-50/70 via-emerald-50/30 to-emerald-100/10 border-emerald-200/80 dark:from-slate-900/90 dark:via-emerald-950/35 dark:to-emerald-950/55 dark:border-emerald-800/60 shadow-[0_4px_20px_-4px_rgba(16,185,129,0.08)]';
      case 'indigo':
        return 'bg-gradient-to-br from-indigo-50/70 via-indigo-50/30 to-indigo-100/10 border-indigo-200/80 dark:from-slate-900/90 dark:via-indigo-950/35 dark:to-indigo-950/55 dark:border-indigo-800/60 shadow-[0_4px_20px_-4px_rgba(99,102,241,0.08)]';
      default:
        return 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-850';
    }
  };
  const [podcasts, setPodcasts] = useState<Podcast[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSeries, setActiveSeries] = useState<Podcast | null>(null);
  const [seriesEpisodes, setSeriesEpisodes] = useState<RSSEpisode[]>([]);
  const [isSeriesLoading, setIsSeriesLoading] = useState(false);
  const [activeEmbed, setActiveEmbed] = useState<{title: string, html: string} | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [episodeSearchTerm, setEpisodeSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const data = await StorageService.getPodcasts(certId || 'cna');
        setPodcasts(data);
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [certId]);

  const handleOpenSeries = async (series: Podcast) => {
    setActiveSeries(series);
    setEpisodeSearchTerm('');
    if (series.isRssFeed && series.rssUrl) {
        setIsSeriesLoading(true);
        try {
            // FIXED: Path includes service prefix for GCP routing
            const response = await fetch(`/practice-exams/api/proxy/rss?url=${encodeURIComponent(series.rssUrl)}`);
            const xmlText = await response.text();
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlText, "text/xml");
            
            const getTag = (el: Element, name: string) => {
                return el.querySelector(name)?.textContent || 
                       el.getElementsByTagName(`itunes:${name}`)[0]?.textContent || "";
            };

            const getAttr = (el: Element, name: string, attr: string) => {
                const standard = el.querySelector(name);
                if (standard && standard.getAttribute(attr)) return standard.getAttribute(attr);
                const itunes = el.getElementsByTagName(`itunes:${name}`)[0];
                return itunes ? itunes.getAttribute(attr) : "";
            };

            const channelImg = xmlDoc.querySelector("channel > image > url")?.textContent || 
                               getAttr(xmlDoc.querySelector("channel") as Element, "image", "href") || series.imageUrl;

            const items = xmlDoc.querySelectorAll("item");
            const episodes: RSSEpisode[] = Array.from(items).map(item => ({
                title: getTag(item, "title") || "Untitled",
                description: (getTag(item, "description") || getTag(item, "summary")).replace(/<[^>]*>?/gm, ''),
                sourceUrl: item.querySelector("enclosure")?.getAttribute("url") || "",
                pubDate: getTag(item, "pubDate"),
                duration: formatRssDuration(getTag(item, "duration")) || "--:--",
                imageUrl: getAttr(item, "image", "href") || (channelImg || '')
            }));
            setSeriesEpisodes(episodes);
        } catch (e) {
            console.error("Failed to sync series episodes", e);
        } finally {
            setIsSeriesLoading(false);
        }
    } else {
        setSeriesEpisodes([]);
    }
  };

  const handlePlayAction = (podcast: Podcast | RSSEpisode) => {
      // Track the audio_play event in telemetry
      StorageService.trackUserEvent({
          action: 'audio_play',
          category: 'podcast',
          label: podcast.title,
          metadata: { isRSS: !('sourceType' in podcast) }
      });

      if ('sourceUrl' in podcast && !('sourceType' in podcast)) {
          // This is an RSSEpisode
          playTrack({
              title: podcast.title,
              img: podcast.imageUrl,
              source: (podcast as RSSEpisode).sourceUrl,
              duration: podcast.duration
          });
          return;
      }

      const p = podcast as Podcast;
      if (p.sourceType === 'embed' && p.embedHtml) {
          setActiveEmbed({ title: p.title, html: p.embedHtml });
      } else if (p.sourceUrl) {
          playTrack({
              title: p.title,
              img: p.imageUrl || '',
              source: p.sourceUrl,
              duration: p.duration
          });
      }
  };

  const [activeCategoryTab, setActiveCategoryTab] = useState<string>('all');

  const filteredPodcasts = useMemo(() => {
    return podcasts.filter(p => {
      const matchesSearch = (p.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (p.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      if (activeCategoryTab === 'all') return matchesSearch;
      const cat = getPodcastCategory(p).name;
      return matchesSearch && cat === activeCategoryTab;
    });
  }, [podcasts, searchTerm, activeCategoryTab]);

  const filteredEpisodes = useMemo(() => {
    if (!episodeSearchTerm) return seriesEpisodes;
    return seriesEpisodes.filter(ep => 
      (ep.title || '').toLowerCase().includes(episodeSearchTerm.toLowerCase()) ||
      (ep.description || '').toLowerCase().includes(episodeSearchTerm.toLowerCase())
    );
  }, [seriesEpisodes, episodeSearchTerm]);

  const uniqueCategories = useMemo(() => {
    const categoriesSet = new Set(podcasts.map(p => getPodcastCategory(p).name));
    return Array.from(categoriesSet).sort();
  }, [podcasts]);

  const PlayingIndicator = () => (
    <div className="absolute bottom-3 right-3 flex gap-1 items-end h-6 bg-slate-900/60 backdrop-blur-md px-2 py-1 rounded-lg">
        <div className="w-1 bg-indigo-400 animate-[bounce_1s_infinite] h-2"></div>
        <div className="w-1 bg-indigo-400 animate-[bounce_0.8s_infinite] h-5"></div>
        <div className="w-1 bg-indigo-400 animate-[bounce_1.2s_infinite] h-3"></div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto pb-32 animate-fade-in text-slate-900 dark:text-slate-100">
      <AdUnit placement="podcasts_top" />
      
      {!activeSeries && (
        <ModuleHeader 
          icon={Mic2}
          title="Audio Lessons"
          description="Listen to clear audio lessons on the go. Perfect for studying while walking, driving, or relaxing."
          badge="Listen & Learn"
          color="indigo"
        />
      )}

      {/* EMBED MODAL PLAYER */}
      {activeEmbed && (
          <div className="fixed inset-0 z-[200] bg-slate-950/95 backdrop-blur-xl flex items-start justify-center p-4 md:p-8 pt-10 md:pt-16 animate-fade-in overflow-y-auto custom-scrollbar">
              <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl w-full max-w-2xl h-auto overflow-hidden animate-pop border border-slate-100 dark:border-slate-800">
                  <div className="p-6 border-b border-slate-100 dark:border-slate-850 flex justify-between items-center bg-slate-50/50 dark:bg-slate-950/50">
                      <div className="flex items-center gap-3">
                          <div className="p-2.5 bg-indigo-600 text-white rounded-xl shadow-lg animate-pulse">
                              <Radio size={18}/>
                          </div>
                          <div className="min-w-0">
                              <h3 className="text-lg font-black tracking-tight text-slate-900 dark:text-white line-clamp-1 max-w-[240px] md:max-w-[400px]">{activeEmbed.title}</h3>
                              <p className="text-[9px] font-black text-indigo-600 dark:text-indigo-400 tracking-widest uppercase">Active External Feed</p>
                          </div>
                      </div>
                      <button onClick={() => setActiveEmbed(null)} className="p-2.5 bg-slate-50 dark:bg-slate-800 text-slate-400 hover:text-rose-500 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700 transition-all shrink-0"><X size={18}/></button>
                  </div>
                  <div className="p-8 bg-slate-950 min-h-[300px] flex items-center justify-center relative overflow-hidden">
                      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.4)_0%,transparent_100%)]"></div>
                      <div className="relative z-10 w-full" dangerouslySetInnerHTML={{ __html: activeEmbed.html }} />
                  </div>
                  <div className="p-6 bg-slate-50 dark:bg-slate-950 border-t border-slate-100 dark:border-slate-850 flex items-center justify-between">
                       <div className="flex items-center gap-2">
                           <ShieldCheck className="text-emerald-500" size={16}/>
                           <span className="text-[10px] font-black text-slate-500 dark:text-slate-400 tracking-widest uppercase">Verified Ingest Engine</span>
                       </div>
                       <button onClick={() => setActiveEmbed(null)} className="px-6 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-950 rounded-xl font-black text-[10px] tracking-widest hover:scale-105 transition-all uppercase">Close Player</button>
                  </div>
              </div>
          </div>
      )}

      {/* Hero Header Dashboard Console */}
      {!activeSeries && (
        <section className={`${tokens.heroBannerBg} rounded-[2.5rem] p-6 md:p-10 text-white mb-8 shadow-2xl relative overflow-hidden group border border-white/5`}>
            <div className="absolute -bottom-10 -right-10 opacity-5 group-hover:scale-110 group-hover:rotate-6 transition-transform duration-1000 pointer-events-none">
                <Mic2 size={240} />
            </div>
            <div className="relative z-10">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-2.5 mb-4">
                            <div className={`p-2 ${tokens.iconBg} rounded-xl shadow-lg border-b-2 border-black/20`}>
                                <Headphones className="text-white" size={20} />
                            </div>
                            <span className={`text-[10px] font-black tracking-[0.25em] ${tokens.accentText} uppercase`}>Interactive Studio</span>
                        </div>
                        <h1 className="text-3xl md:text-5xl font-black tracking-tighter leading-none mb-3">Podcast & Lectures</h1>
                        <p className="text-slate-300 font-medium text-xs md:text-sm max-w-xl leading-relaxed opacity-95">
                            Synthesize essential nurse theory, local nursing frameworks, and procedural standards via hands-free audio. Optimized for desktop and active transit study.
                        </p>
                    </div>
                    {/* Live streaming status bar widget */}
                    {currentTrack && isPlaying && (
                        <div className="bg-white/5 backdrop-blur-md px-5 py-4 rounded-2xl border border-white/10 flex items-center gap-4 animate-fade-in shrink-0">
                            <div className="relative flex items-center justify-center">
                                <div className={`absolute inset-0 ${tokens.progressBar} opacity-30 rounded-full animate-ping`}></div>
                                <div className={`w-10 h-10 ${tokens.iconBg} rounded-full flex items-center justify-center shrink-0`}>
                                    <Volume2 className="text-white animate-pulse" size={18} />
                                </div>
                            </div>
                            <div className="min-w-0 max-w-[200px]">
                                <span className={`text-[8px] tracking-widest font-black ${tokens.accentText} uppercase block leading-none mb-1`}>Streaming Live</span>
                                <h4 className="text-xs font-black truncate text-white">{currentTrack.title}</h4>
                                <p className="text-[9px] text-slate-400 font-medium">{currentTrack.duration || 'Live Audio'}</p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center gap-4">
                    <div className="relative flex-1 w-full group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-white transition-colors" size={18} />
                        <input 
                            type="text" 
                            placeholder="Filter audio tracks by keyword..." 
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-12 py-3 bg-white/5 border border-white/10 rounded-2xl outline-none focus:ring-4 focus:ring-white/20 text-sm text-white placeholder:text-slate-400 font-bold transition-all shadow-inner"
                        />
                        {searchTerm && (
                            <button 
                                onClick={() => setSearchTerm('')} 
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>
                    <div className="flex gap-1 bg-white/5 p-1 rounded-2xl border border-white/10 shrink-0">
                        <button 
                            onClick={() => setViewMode('list')}
                            className={`p-2.5 rounded-xl transition-all ${viewMode === 'list' ? `${tokens.primaryBtn}` : 'text-slate-400 hover:text-slate-200'}`}
                            title="List Layout"
                        >
                            <List size={18} />
                        </button>
                        <button 
                            onClick={() => setViewMode('grid')}
                            className={`p-2.5 rounded-xl transition-all ${viewMode === 'grid' ? `${tokens.primaryBtn}` : 'text-slate-400 hover:text-slate-200'}`}
                            title="Grid Layout"
                        >
                            <LayoutGrid size={18} />
                        </button>
                    </div>
                </div>
            </div>
        </section>
      )}

      {/* HORIZONTAL CATEGORY NAVIGATION */}
      {!activeSeries && (
        <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-8 select-none no-scrollbar">
          <button 
            onClick={() => setActiveCategoryTab('all')}
            className={`px-5 py-2.5 rounded-full text-[10px] font-black tracking-widest transition-all border shrink-0 uppercase ${
              activeCategoryTab === 'all' 
                ? tokens.activeTab
                : tokens.inactiveTab
            }`}
          >
            All Lessons ({podcasts.length})
          </button>
          
          {uniqueCategories.map(cat => {
            const sample = podcasts.find(p => getPodcastCategory(p).name === cat);
            if (!sample) return null;
            const count = podcasts.filter(p => getPodcastCategory(p).name === cat).length;
            const catInfo = getPodcastCategory(sample);
            const CatIcon = catInfo.icon;
            
            return (
              <button 
                key={cat}
                onClick={() => setActiveCategoryTab(cat)}
                className={`px-5 py-2.5 rounded-full text-[10px] font-black tracking-widest transition-all border shrink-0 uppercase flex items-center gap-2.5 ${
                  activeCategoryTab === cat 
                    ? tokens.activeTab 
                    : tokens.inactiveTab
                }`}
              >
                <CatIcon size={12} className={activeCategoryTab === cat ? 'text-white' : catInfo.textColor} />
                <span>{cat}</span>
                <span className={`text-[8px] px-1.5 py-0.5 rounded-md ${activeCategoryTab === cat ? 'bg-black/20 text-white' : 'bg-slate-200 dark:bg-slate-800 text-slate-600'}`}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {activeSeries ? (
        <div className="animate-fade-in space-y-8 pb-20">
            <div className="flex justify-between items-center px-1">
                <button onClick={() => setActiveSeries(null)} className="flex items-center gap-2 text-[10px] font-black tracking-widest text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors group uppercase">
                    <ChevronLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> Back to Catalog
                </button>
                <div className="flex gap-1.5 bg-slate-100 dark:bg-slate-900 p-1 rounded-xl border border-slate-200 dark:border-slate-800">
                    <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-slate-400'}`}><List size={16}/></button>
                    <button onClick={() => setViewMode('grid')} className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-slate-400'}`}><LayoutGrid size={16}/></button>
                </div>
            </div>

            {/* Premium Header representing Spotify/Podcast series banner */}
            <section className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-6 md:p-10 border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col md:flex-row gap-8 md:gap-10 items-center relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-50 dark:bg-indigo-950/20 blur-[100px] pointer-events-none rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="w-44 h-44 md:w-56 md:h-56 rounded-[2rem] overflow-hidden shadow-xl shrink-0 border-4 border-slate-50 dark:border-slate-800 group-hover:scale-[1.02] transition-transform duration-700">
                    <img src={activeSeries.imageUrl || 'https://images.unsplash.com/photo-1478737270239-2fccd27ee8f9?auto=format&fit=crop&q=80&w=400'} className="w-full h-full object-cover" alt={activeSeries.title} />
                </div>
                <div className="flex-1 text-center md:text-left relative z-10 min-w-0">
                    <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
                        <span className="px-3.5 py-1 bg-indigo-600 dark:bg-indigo-500 text-white rounded-xl text-[9px] font-black tracking-widest uppercase shadow-md">Linked Channel</span>
                        <span className="text-[10px] font-black text-slate-400 dark:text-slate-400 flex items-center gap-1.5"><Globe size={13} className="text-indigo-500"/> Synced via RSS</span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white tracking-tighter mb-3 leading-tight line-clamp-2">{activeSeries.title}</h2>
                    <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 font-medium leading-relaxed max-w-xl line-clamp-3">"{activeSeries.description}"</p>
                </div>
            </section>

            <div className="space-y-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-2">
                    <div className="flex items-center gap-2.5">
                        <div className="p-1.5 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 rounded-lg animate-pulse"><Radio size={16}/></div>
                        <h3 className="text-[10px] font-black text-slate-400 dark:text-slate-400 tracking-[0.2em] uppercase">Automatic Feed Sequence</h3>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                        <div className="relative w-full sm:w-64 group">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={14} />
                            <input 
                                type="text" 
                                placeholder="Search episodes..." 
                                value={episodeSearchTerm}
                                onChange={e => setEpisodeSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-8 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl outline-none focus:ring-4 focus:ring-indigo-500/10 text-xs font-bold transition-all text-slate-900 dark:text-white"
                            />
                            {episodeSearchTerm && (
                                <button 
                                    onClick={() => setEpisodeSearchTerm('')} 
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                                >
                                    <X size={12} />
                                </button>
                            )}
                        </div>
                        <span className="text-[10px] font-black text-slate-400 dark:text-slate-400 uppercase tracking-widest shrink-0">
                            {filteredEpisodes.length} of {seriesEpisodes.length} Episodes
                        </span>
                    </div>
                </div>

                {isSeriesLoading ? (
                    <PageLoader message="Establishing RSS Logic Bridge..." />
                ) : filteredEpisodes.length === 0 ? (
                    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-16 text-center border-2 border-dashed border-slate-100 dark:border-slate-800 flex flex-col items-center gap-4 opacity-80 animate-fade-in">
                        <div className="w-14 h-14 bg-slate-50 dark:bg-slate-950 rounded-full flex items-center justify-center shadow-inner">
                            <Search size={22} className="text-slate-300 dark:text-slate-600" />
                        </div>
                        <div className="space-y-1">
                            <p className="text-sm font-black text-slate-700 dark:text-white">No Matching Episodes</p>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">"No broadcast items match your search term in this channel."</p>
                        </div>
                        <button onClick={() => setEpisodeSearchTerm('')} className="px-5 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all">Clear Search Filter</button>
                    </div>
                ) : (
                    <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
                        {filteredEpisodes.map((ep, idx) => {
                            const isThisPlaying = currentTrack?.source === ep.sourceUrl && isPlaying;
                            
                            if (viewMode === 'grid') {
                                return (
                                    <article key={idx} className={`bg-white dark:bg-slate-900 rounded-[2rem] border-2 transition-all hover:shadow-xl group overflow-hidden ${isThisPlaying ? 'border-indigo-500 ring-4 ring-indigo-500/5' : 'border-slate-100 dark:border-slate-800 shadow-sm'}`}>
                                        <div className="aspect-square relative overflow-hidden border-b border-slate-100 dark:border-slate-800 bg-slate-100 dark:bg-slate-950">
                                            <img src={ep.imageUrl} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt={ep.title} />
                                            <div className="absolute inset-0 bg-slate-950/55 backdrop-blur-[1px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                                                <button onClick={() => handlePlayAction(ep)} className="w-14 h-14 bg-white dark:bg-indigo-600 text-indigo-600 dark:text-white rounded-full flex items-center justify-center shadow-2xl active:scale-90 transition-all hover:scale-110">
                                                    {isThisPlaying ? <Pause size={24} /> : <Play size={24} fill="currentColor" className="ml-1" />}
                                                </button>
                                            </div>
                                            {isThisPlaying && <PlayingIndicator />}
                                        </div>
                                        <div className="p-5 space-y-3">
                                            <div className="flex items-center justify-between text-[9px] font-bold text-slate-400">
                                                <span className="flex items-center gap-1"><Calendar size={11}/> {new Date(ep.pubDate).toLocaleDateString()}</span>
                                                <span className="text-indigo-500 font-extrabold flex items-center gap-1"><Clock size={11}/> {ep.duration}</span>
                                            </div>
                                            <h4 className="font-black text-slate-800 dark:text-white text-xs line-clamp-2 leading-tight min-h-[32px]">{ep.title}</h4>
                                            <button onClick={() => handlePlayAction(ep)} className={`w-full py-2.5 rounded-xl font-black text-[9px] tracking-widest uppercase transition-all active:scale-95 ${isThisPlaying ? 'bg-indigo-600 text-white shadow-lg' : 'bg-slate-950 dark:bg-slate-800 text-white hover:bg-black dark:hover:bg-slate-700'}`}>
                                                {isThisPlaying ? 'Stop Track' : 'Start Track'}
                                            </button>
                                        </div>
                                    </article>
                                );
                            }

                            return (
                                <article key={idx} className="bg-white dark:bg-slate-900 p-4 rounded-[2rem] border border-slate-100 dark:border-slate-800 hover:shadow-lg hover:border-indigo-100 dark:hover:border-indigo-950/70 transition-all flex flex-col md:flex-row gap-5 items-center group relative overflow-hidden">
                                    <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 shadow-md group-hover:scale-105 transition-transform border-4 border-white dark:border-slate-800 relative bg-slate-100 dark:bg-slate-950">
                                        <img src={ep.imageUrl} className="w-full h-full object-cover" alt={ep.title} />
                                        {isThisPlaying && <div className="absolute inset-0 bg-indigo-600/20"></div>}
                                    </div>
                                    <div className="flex-1 text-center md:text-left min-w-0">
                                        <div className="flex items-center justify-center md:justify-start gap-4 mb-1">
                                            <span className="text-[9px] font-bold text-slate-400 flex items-center gap-1"><Calendar size={11}/> {new Date(ep.pubDate).toLocaleDateString()}</span>
                                            <span className="text-[9px] font-black text-indigo-500 flex items-center gap-1"><Clock size={11}/> {ep.duration}</span>
                                        </div>
                                        <h4 className="font-black text-slate-805 dark:text-white text-sm line-clamp-2 pr-4 leading-snug">{ep.title}</h4>
                                        <p className="text-xs text-slate-400 font-medium line-clamp-1 mt-1 leading-relaxed opacity-90 group-hover:opacity-100 transition-opacity">"{ep.description}"</p>
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0">
                                        {isThisPlaying && <div className="hidden md:block mr-2 scale-75"><PlayingIndicator /></div>}
                                        <button 
                                            onClick={() => handlePlayAction(ep)}
                                            className={`px-8 py-3 rounded-xl font-black text-[9px] tracking-widest uppercase transition-all active:scale-95 shadow-md shrink-0 flex items-center gap-2 border-b-2 ${isThisPlaying ? 'bg-indigo-600 border-indigo-800 text-white' : 'bg-slate-950 border-black text-white hover:bg-black dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-slate-755'}`}
                                        >
                                            {isThisPlaying ? <Pause size={14} /> : <Play size={14} fill="currentColor" />}
                                            {isThisPlaying ? 'Pause' : 'Play Track'}
                                        </button>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            {isLoading ? (
              <PageLoader message="Synchronizing Audio Registry..." />
            ) : filteredPodcasts.length === 0 ? (
              <div className="bg-white dark:bg-slate-900 rounded-[3rem] p-16 md:p-24 text-center border-4 border-dashed border-slate-100 dark:border-slate-800 flex flex-col items-center gap-6 opacity-80 animate-fade-in">
                  <div className="w-20 h-20 bg-slate-50 dark:bg-slate-950 rounded-full flex items-center justify-center shadow-inner">
                      <PodcastIcon size={38} className="text-slate-350 dark:text-slate-600" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-xl font-black text-slate-900 dark:text-white tracking-tight">No Audio Lessons Found</p>
                    <p className="text-xs font-bold text-slate-400 tracking-widest uppercase">"Awaiting operational broadcast ingestion in this category."</p>
                  </div>
                  {searchTerm && (
                      <button onClick={() => setSearchTerm('')} className="px-6 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-[10px] tracking-widest uppercase font-black transition-all">
                          Clear Search Filter
                      </button>
                  )}
              </div>
            ) : (
              <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in' : 'space-y-5 animate-fade-in'}>
                {filteredPodcasts.map(podcast => {
                    const isThisPlaying = currentTrack?.source === podcast.sourceUrl && isPlaying;
                    const catInfo = getPodcastCategory(podcast);
                    const CatIcon = catInfo.icon;
                    
                    if (viewMode === 'grid') {
                        return (
                            <article 
                                key={podcast._id}
                                className={`group rounded-[2rem] border-2 transition-all hover:shadow-xl overflow-hidden flex flex-col ${getPodcastCardBgClass(catInfo.color)} ${activeSeries?._id === podcast._id || isThisPlaying ? 'border-indigo-500 dark:border-indigo-400 shadow-lg shadow-indigo-600/5' : 'shadow-sm'}`}
                            >
                                <div className="aspect-video relative overflow-hidden border-b border-slate-55 dark:border-slate-850 bg-slate-100 dark:bg-slate-950">
                                    <img src={podcast.imageUrl || 'https://images.unsplash.com/photo-1478737270239-2fccd27ee8f9?auto=format&fit=crop&q=80&w=400'} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" alt={podcast.title} />
                                    <div className="absolute inset-0 bg-slate-950/50 backdrop-blur-[1.5px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                                        {podcast.isRssFeed ? (
                                            <button onClick={() => handleOpenSeries(podcast)} className="w-14 h-14 bg-white text-indigo-600 rounded-full flex items-center justify-center shadow-2xl active:scale-90 transition-all hover:scale-110"><Layout size={24}/></button>
                                        ) : (
                                            <button onClick={() => handlePlayAction(podcast)} className="w-14 h-14 bg-white text-slate-950 rounded-full flex items-center justify-center shadow-2xl active:scale-90 transition-all hover:scale-110">
                                                {podcast.sourceType === 'embed' ? <Eye size={24}/> : (isThisPlaying ? <Pause size={24} /> : <Play size={24} fill="currentColor" className="ml-1" />)}
                                            </button>
                                        )}
                                    </div>
                                    {isThisPlaying && <PlayingIndicator />}
                                    <div className="absolute top-4 left-4 flex flex-col gap-1.5 items-start">
                                        <span className={`px-2.5 py-1 rounded-lg text-[8px] font-black tracking-widest uppercase shadow-md flex items-center gap-1.5 ${catInfo.badgeColor}`}>
                                            <CatIcon size={10} />
                                            <span>{catInfo.name}</span>
                                        </span>
                                        <span className={`px-2.5 py-0.5 rounded-md text-[7px] font-black tracking-widest uppercase shadow-sm bg-slate-950 text-white`}>
                                            {podcast.isRssFeed ? 'Series Channel' : 'Lesson Track'}
                                        </span>
                                    </div>
                                </div>
                                <div className="p-6 flex-1 flex flex-col justify-between">
                                    <div>
                                        <h3 className="text-lg font-black text-slate-905 dark:text-white tracking-tight mb-2 line-clamp-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors leading-tight min-h-[44px]">{podcast.title}</h3>
                                        <p className="text-xs text-slate-400 dark:text-slate-450 font-medium line-clamp-2 mb-4 leading-relaxed">"{podcast.description}"</p>
                                    </div>
                                    <div className="flex items-center justify-between pt-4 border-t border-slate-50 dark:border-slate-850 mt-4">
                                        <span className="flex items-center gap-1.5 text-slate-400 text-[10px] font-black"><Clock size={12}/> {podcast.duration || 'Variable Duration'}</span>
                                        {podcast.isRssFeed ? (
                                            <button onClick={() => handleOpenSeries(podcast)} className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 flex items-center gap-0.5 hover:underline uppercase tracking-wide">Explore <ChevronRight size={12}/></button>
                                        ) : (
                                            <button onClick={() => handlePlayAction(podcast)} className="text-[10px] font-black text-slate-900 dark:text-white flex items-center gap-0.5 hover:underline uppercase tracking-wide">
                                                {isThisPlaying ? 'Pause' : 'Play'} <ChevronRight size={12}/>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </article>
                        );
                    }

                    return (
                        <article 
                        key={podcast._id}
                        className={`group p-5 rounded-[2.2rem] border-2 transition-all hover:shadow-xl flex flex-col md:flex-row gap-6 md:gap-8 items-center relative overflow-hidden ${getPodcastCardBgClass(catInfo.color)} ${activeSeries?._id === podcast._id || isThisPlaying ? 'border-indigo-500 dark:border-indigo-400 shadow-md' : 'shadow-sm'}`}
                        >
                        <div className="relative w-32 h-32 md:w-36 md:h-36 shrink-0 rounded-[1.8rem] overflow-hidden shadow-md border-4 border-white dark:border-slate-800 group-hover:scale-105 transition-transform duration-700 bg-slate-100 dark:bg-slate-950">
                            <img src={podcast.imageUrl || 'https://images.unsplash.com/photo-1478737270239-2fccd27ee8f9?auto=format&fit=crop&q=80&w=400'} className="w-full h-full object-cover/100" alt={podcast.title} />
                            <div className="absolute inset-0 bg-slate-950/50 backdrop-blur-[1px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                            {podcast.isRssFeed ? (
                                <button onClick={() => handleOpenSeries(podcast)} className="w-12 h-12 bg-white text-indigo-600 rounded-full flex items-center justify-center shadow-2xl active:scale-95 transition-all hover:scale-110"><Layout size={20}/></button>
                            ) : (
                                <button onClick={() => handlePlayAction(podcast)} className="w-12 h-12 bg-white text-slate-950 rounded-full flex items-center justify-center shadow-2xl active:scale-95 transition-all hover:scale-110">
                                    {podcast.sourceType === 'embed' ? <Eye size={20}/> : (isThisPlaying ? <Pause size={20} /> : <Play size={20} fill="currentColor" className="ml-0.5" />)}
                                </button>
                            )}
                            </div>
                            {isThisPlaying && <PlayingIndicator />}
                        </div>
                        
                        <div className="flex-1 text-center md:text-left min-w-0">
                            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-3">
                                <span className={`px-2.5 py-1 rounded-lg text-[8px] font-black tracking-widest uppercase shadow-sm flex items-center gap-1.5 ${catInfo.badgeColor}`}>
                                    <CatIcon size={10} />
                                    <span>{catInfo.name}</span>
                                </span>
                                <span className={`px-2.5 py-0.5 rounded-md text-[7px] font-black tracking-widest uppercase bg-slate-950 text-white shadow-inner`}>
                                    {podcast.isRssFeed ? 'Series Feed' : 'Single Lesson'}
                                </span>
                                <span className="flex items-center gap-1 text-slate-400 text-[10px] font-black uppercase"><Clock size={12} className="text-indigo-500"/> {podcast.duration || 'Variable Duration'}</span>
                            </div>
                            <h3 className="text-xl md:text-2xl font-black text-slate-905 dark:text-white tracking-tight mb-2 leading-snug group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors line-clamp-1">{podcast.title}</h3>
                            <p className="text-xs md:text-sm text-slate-450 font-medium line-clamp-2 leading-relaxed mb-4 opacity-90 group-hover:opacity-100 transition-opacity">"{podcast.description}"</p>
                            
                            <div className="flex flex-wrap items-center justify-center md:justify-start gap-4">
                            {podcast.isRssFeed ? (
                                <button onClick={() => handleOpenSeries(podcast)} className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-[10px] tracking-widest uppercase flex items-center gap-2 transition-all active:scale-95 shadow-md border-b-2 border-indigo-800">Browse Full Series <ChevronRight size={14}/></button>
                            ) : (
                                <button onClick={() => handlePlayAction(podcast)}
                                    className={`px-8 py-3 rounded-xl font-black text-[10px] tracking-widest uppercase flex items-center gap-2 transition-all active:scale-95 shadow-md border-b-2 ${isThisPlaying ? 'bg-indigo-600 border-indigo-800 text-white' : 'bg-slate-950 border-black text-white hover:bg-black dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-slate-755'}`}>
                                    {podcast.sourceType === 'embed' ? <><Code size={14}/> Open Player</> : (isThisPlaying ? <><Pause size={14}/> Pause</> : <><Play size={14} fill="currentColor"/> Play Track</>)}
                                </button>
                            )}
                            </div>
                        </div>
                        </article>
                    );
                })}
              </div>
            )}
          </div>

          <div className="lg:col-span-4 space-y-8 animate-fade-in delay-100">
             <AdUnit placement="podcasts_sidebar" />
             <section className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 border border-slate-200 dark:border-slate-805 shadow-sm relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-[0.03] group-hover:rotate-12 transition-transform duration-1000">
                  <Activity size={120} />
                </div>
                <h4 className="text-xs font-black text-slate-400 dark:text-slate-400 tracking-[0.2em] mb-6 flex items-center gap-2 uppercase">
                  <Target size={16} className="text-indigo-600" /> Operational Metrics
                </h4>
                <div className="space-y-3.5">
                  {[
                    { label: 'Database Protocol', value: 'ONLINE & SECURE', color: 'emerald' },
                    { label: 'Dynamic Series', value: podcasts.filter(p => p.isRssFeed).length.toString(), color: 'indigo' },
                    { label: 'Custom Tracks', value: podcasts.filter(p => !p.isRssFeed).length.toString(), color: 'amber' }
                  ].map((stat, i) => (
                    <div key={i} className="p-4 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-850 flex items-center justify-between group-hover:border-indigo-200/50 dark:group-hover:border-indigo-950 transition-all group-hover:bg-white dark:group-hover:bg-slate-900 shadow-inner group-hover:shadow-sm">
                      <span className="text-[9px] font-black text-slate-500 tracking-widest uppercase">{stat.label}</span>
                      <span className={`text-[10px] font-black text-${stat.color}-600 dark:text-${stat.color}-400`}>{stat.value}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 p-5 bg-indigo-50/70 dark:bg-indigo-950/15 rounded-2xl border border-indigo-100/30 dark:border-indigo-950/50 flex items-start gap-3">
                    <div className="p-1.5 shrink-0 bg-indigo-600 text-white rounded-lg shadow-md">
                        <Sparkles size={14}/>
                    </div>
                    <p className="text-[10px] font-bold text-indigo-900 dark:text-indigo-300 leading-normal opacity-90">
                        "Audio training during idle periods is proven to increase recall by 42% for procedural clinical steps."
                    </p>
                </div>
             </section>

             <AdUnit placement="sidebar" />
          </div>
        </div>
      )}
    </div>
  );
};